import React, { useState } from 'react';
import { Mail, CheckCircle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { db } from '../firstSnap/config/firebase-config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import ThankYouModal from './ThankYouModal';

const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [submittedName, setSubmittedName] = useState('');
  const { teamColors, selectedTeam } = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !name) return;
    
    try {
      // Always call MailerLite API first
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3001/api/subscribe'
        : '/api/subscribe';
      
      const mailerLiteResponse = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          name,
          team: selectedTeam?.name || '', 
          source: 'website' 
        })
      });

      if (!mailerLiteResponse.ok) {
        console.error('MailerLite API failed:', await mailerLiteResponse.text());
      }

      // Also save to Firestore for backup
      try {
        await addDoc(collection(db, 'newsletter_signups'), {
          email,
          name,
          team: selectedTeam?.name || '',
          source: 'website',
          createdAt: serverTimestamp(),
        });
      } catch (firestoreError) {
        console.warn('Firestore save failed:', firestoreError);
      }

      setSubmittedName(name);
      setIsSubmitted(true);
      setShowThankYouModal(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setEmail('');
        setName('');
        setShowThankYouModal(false);
      }, 7000);
    } catch (error) {
      console.error('Newsletter signup failed:', error);
      // Still show success to user even if API fails
      setSubmittedName(name);
      setIsSubmitted(true);
      setShowThankYouModal(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setEmail('');
        setName('');
        setShowThankYouModal(false);
      }, 7000);
    }
  };

  return (
    <section 
      className="py-16 relative overflow-hidden football-field-subtle"
      style={{
        background: selectedTeam 
          ? `linear-gradient(to right, ${teamColors.primary} 0%, ${teamColors.secondary} 50%, ${teamColors.primary} 100%)`
          : 'linear-gradient(to right, #0ea5e9 0%, #f97316 50%, #0ea5e9 100%)'
      }}
    >
      {/* Football field background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        {/* End zone lines */}
        <div 
          className="absolute top-0 left-0 w-full h-3 opacity-30"
          style={{ backgroundColor: teamColors.accent }}
        ></div>
        <div 
          className="absolute bottom-0 left-0 w-full h-3 opacity-30"
          style={{ backgroundColor: teamColors.accent }}
        ></div>
        
        {/* Yard line markers */}
        <div 
          className="absolute top-0 left-1/4 w-2 h-full opacity-25"
          style={{ backgroundColor: teamColors.accent }}
        ></div>
        <div 
          className="absolute top-0 left-2/4 w-3 h-full opacity-30"
          style={{ backgroundColor: teamColors.accent }}
        ></div>
        <div 
          className="absolute top-0 left-3/4 w-2 h-full opacity-25"
          style={{ backgroundColor: teamColors.accent }}
        ></div>
        
        {/* Goal posts */}
        <div 
          className="absolute top-16 left-16 w-10 h-10 goalpost opacity-40"
          style={{ backgroundColor: teamColors.accent }}
        ></div>
        <div 
          className="absolute bottom-16 right-16 w-10 h-10 goalpost opacity-40"
          style={{ backgroundColor: teamColors.accent }}
        ></div>
        
        {/* Football decorations */}
        <div 
          className="absolute top-20 right-20 w-14 h-10 football-shape football-laces opacity-30 animate-float"
          style={{ backgroundColor: teamColors.accent }}
        ></div>
        <div 
          className="absolute bottom-20 left-20 w-12 h-8 football-shape football-laces opacity-30 animate-float"
          style={{ 
            backgroundColor: teamColors.accent,
            animationDelay: '1.5s'
          }}
        ></div>
        
        {/* Hash marks */}
        <div 
          className="absolute top-12 left-0 w-full h-1 opacity-25"
          style={{ backgroundColor: teamColors.accent }}
        ></div>
        <div 
          className="absolute bottom-12 left-0 w-full h-1 opacity-25"
          style={{ backgroundColor: teamColors.accent }}
        ></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">

        <div className="max-w-4xl mx-auto">

          {/* Main signup form with enhanced theming */}
          <div 
            className="card-field p-8 lg:p-12 relative overflow-hidden shadow-2xl"
            style={{
              borderTop: `4px solid ${teamColors.accent}`,
              borderLeft: `2px solid ${teamColors.accent}30`
            }}
          >
            {/* Field pattern decorations */}
            <div className="absolute inset-0 pointer-events-none">
              {/* Corner yard markers */}
              <div 
                className="absolute top-0 left-0 w-12 h-12 opacity-15"
                style={{ 
                  backgroundColor: teamColors.accent,
                  clipPath: 'polygon(0 0, 100% 0, 0 100%)'
                }}
              ></div>
              <div 
                className="absolute bottom-0 right-0 w-12 h-12 opacity-15"
                style={{ 
                  backgroundColor: teamColors.accent,
                  clipPath: 'polygon(100% 0, 100% 100%, 0 100%)'
                }}
              ></div>
              
              {/* Football field lines */}
              <div 
                className="absolute top-1/3 left-0 w-full h-1 opacity-10"
                style={{ backgroundColor: teamColors.accent }}
              ></div>
              <div 
                className="absolute bottom-1/3 left-0 w-full h-1 opacity-10"
                style={{ backgroundColor: teamColors.accent }}
              ></div>
            </div>

            <div className="relative z-10">
              {!isSubmitted ? (
                <>
                  <div className="text-center mb-8">
                    <div 
                      className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 shadow-xl"
                      style={{ backgroundColor: teamColors.accent }}
                    >
                      <Mail size={40} style={{ color: teamColors.primary }} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      Join the 40 Bags Community
                    </h3>
                    <p className="text-gray-600">
                      Get insider access to exclusive ticket deals, travel tips, and game day experiences with fellow {selectedTeam?.name || 'team'} fans
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Enter your name"
                          className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 text-lg mb-4"
                          style={{ '--tw-ring-color': teamColors.accent }}
                          required
                        />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter your email address"
                          className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 text-lg"
                          style={{ '--tw-ring-color': teamColors.accent }}
                          required
                        />
                      </div>
                      <button
                        type="submit"
                        className="px-8 py-4 font-semibold text-white rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl self-start"
                        style={{
                          background: `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`
                        }}
                      >
                        Get Game Ready
                      </button>
                    </div>
                    
                    <p className="text-xs text-gray-400 text-center">
                      No spam, unsubscribe anytime. We respect your privacy.
                    </p>
                  </form>

                  {/* Benefits list with team styling */}
                  <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      "🎫 Exclusive ticket deals",
                      "🏈 Game day travel guides", 
                      "👥 Connect with local fan groups",
                      "🍺 Tailgate party invites"
                    ].map((benefit, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <div 
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: teamColors.accent }}
                        ></div>
                        <span className="text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <div 
                    className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 animate-pulse-team"
                    style={{ backgroundColor: teamColors.accent }}
                  >
                    <CheckCircle size={40} style={{ color: teamColors.primary }} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Welcome to the Team! 🏈
                  </h3>
                  <p className="text-gray-600 mb-4">
                    You're now part of the 40bags community. Check your inbox for your welcome guide!
                  </p>
                  <div className="flex justify-center space-x-2">
                    <div 
                      className="w-3 h-3 rounded-full animate-bounce"
                      style={{ backgroundColor: teamColors.accent }}
                    ></div>
                    <div 
                      className="w-3 h-3 rounded-full animate-bounce"
                      style={{ 
                        backgroundColor: teamColors.accent,
                        animationDelay: '0.1s'
                      }}
                    ></div>
                    <div 
                      className="w-3 h-3 rounded-full animate-bounce"
                      style={{ 
                        backgroundColor: teamColors.accent,
                        animationDelay: '0.2s'
                      }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Thank You Modal */}
      <ThankYouModal 
        isOpen={showThankYouModal}
        onClose={() => setShowThankYouModal(false)}
        userName={submittedName}
      />
    </section>
  );
};

export default NewsletterSignup; 